import { createSlice } from '@reduxjs/toolkit';
import {
  demographicSectionAddPayload,
  demographicSectionUpdatePayload,
  formatAddressFromResponse,
  therapyDataPayload,
} from './payloads';
import { NO_DATA } from '@src/GlobalConstants';
import { getUniqueEntries } from '@utils';
import { THERAPY_DATA_CODE } from '@utils/constants/medicalCodes';
import { getInsuranceState } from '@pages/Patients/patientInsuranceUtils';
import {
  defaultFormData1,
  defaultFormData2,
  defaultFormData3,
  htnDisordersOptions,
  hypertensionOptions,
  stepsTakenConfig,
} from './constants';

const normalizeDemographicField = (input) => {
  const value = typeof input === 'object' && input !== null ? input.value : input;

  return value && value.trim() !== '' ? value : undefined;
};

const { SYSTOLIC_BP_AT_ENROLL, DIASTOLIC_BP_AT_ENROLL, BP_PANEL_AT_ENROLL } = THERAPY_DATA_CODE;

const initialState = {
  demographic: {
    demographicData: {},
    demographicResponseData: {},
    payload: {},
  },
  hypertension: {
    hypertensionData: {},
    hypertensionResponseData: {},
    patientMedicalHistoryData: {
      hypertensionDiagnosis: [],
    },
    transformedPatientHypertensionResponseData: {},
    // NOTE patientPathwayTemplates is NOT a hypertension call. it's a follow-up call
    // that proceeds after we get pathway template id's from patientPathwayInstanceEndpoint,
    // which is mainly part of patientJourney flow. storing here because the res data is used for
    // display values for CARE PATHWAY on the Hypertension diagnosis & medications card
    patientPathwayTemplatesResponseData: [],
    payload: [],
  },
  observation: {
    observationData: {},
    observationResponseData: {},
    payload: {},
  },
  createdPatientId: null,
  selectedPatientId: null,
  currentPatientId: null,
  createdNameId: {},
  createdTelecomId: null,
  createdPhoneId: null,
  therapyDataFilter: 'BP trend',
  patientTherapyData: {
    keyBPData: {
      DiastolicBP_at_enroll_object: {},
      SystolicBP_at_enroll_object: {},
      DiastolicBPAverage: NO_DATA,
      SystolicBPAverage: NO_DATA,
    },
    patientJourney: {
      patientEngagementOverview: {
        patientPathwayInstanceExists: null,
        patientPathwayInstance: [],
      },
    },
    therapyChartData: {
      averageOverTimeframe: [],
    },
    observations: {
      DiastolicBP_at_enroll: NO_DATA,
      SystolicBP_at_enroll: NO_DATA,
      DiastolicBP_at_enroll_object: {},
      SystolicBP_at_enroll_object: {},
      Cholesterol_total: {},
      Cholesterol_total_updatedAt: null,
      Cholesterol_HDL: {},
      Cholesterol_HDL_updatedAt: null,
      Cholesterol_LDL: {},
      Cholesterol_LDL_updatedAt: null,
      Height_in: NO_DATA,
      Height_in_updatedAt: null,
      Weight_lb: NO_DATA,
      Weight_lb_updatedAt: null,
      HTN_Dx_Duration_Enroll: { valueDateTime: NO_DATA },
    },
    patientHypertensionData: {},
    surveyData: {},
    patientHTNData: {},
    patientInsurance: getInsuranceState(),
    patientEnrollmentReason: {
      value: '',
      payload: {},
    },
  },
  personData: {},
  patientMedicationsData: {},
  patientListData: {
    patientExecuteList: {
      noDataTabPatientExecuteListData: [],
      requireReviewTabPatientExecuteListData: [],
    },
  },
  patientInstructionsListData: {},
  hypertensionInfo: {
    formData1: defaultFormData1,
    formData2: defaultFormData2,
    formData3: defaultFormData3,
  },
};

const patientSlice = createSlice({
  name: 'patient',
  initialState,
  reducers: {
    // TODO move all complex formatting & processing into Actions once we switch to Thunk Actions
    setPatientDemographicData: (state, action) => {
      const {
        data,
        data: { isAddModal, demographicData = {} },
      } = action.payload;

      const {
        address,
        addressId,
        city,
        birthDate,
        email,
        emailId,
        firstName,
        gender,
        lastName,
        nameId,
        patientId,
        patientMrn,
        personId,
        phoneId,
        phone,
        sexForClinicalUse,
        raceEthnicity,
        state: addressState,
        zip,
        externalMedicalRecordNumber,
        withingsUserId,
        preferredTimeZone,
        enrollmentStatus,
        enrolledOn,
        carePathwayType,
        patientPod,
      } = demographicData;

      const demographicAddData = {
        address,
        city,
        birthDate,
        email,
        firstName,
        gender: normalizeDemographicField(gender),
        lastName,
        patientMrn,
        personId,
        podId: normalizeDemographicField(patientPod),
        phone,
        sexForClinicalUse: normalizeDemographicField(sexForClinicalUse),
        raceEthnicity: normalizeDemographicField(raceEthnicity),
        state: addressState,
        zip,
        externalMedicalRecordNumber,
      };

      const demographicUpdateData = {
        address,
        addressId,
        city,
        birthDate,
        email,
        emailId,
        firstName,
        gender: gender?.value || gender,
        lastName,
        nameId,
        patientId,
        patientMrn,
        personId,
        phoneId,
        phone,
        sexForClinicalUse: sexForClinicalUse?.value || sexForClinicalUse,
        raceEthnicity: raceEthnicity?.value || raceEthnicity,
        state: addressState,
        zip,
        externalMedicalRecordNumber,
        withingsUserId,
        preferredTimeZone,
        enrollmentStatus,
        enrolledOn,
        carePathwayType,
      };

      const payload = isAddModal
        ? demographicSectionAddPayload(demographicAddData)
        : demographicSectionUpdatePayload(demographicUpdateData);

      state.demographic = { ...state.demographic, ...data, payload };
    },
    setPatientDemographicResponseData: (state, action) => {
      const address = action.payload?.address;
      const formattedAddress =
        typeof address === 'string'
          ? { address }
          : formatAddressFromResponse(action.payload?.address);

      state.demographic.demographicResponseData = {
        ...state.demographic.demographicResponseData,
        ...action.payload,
        ...formattedAddress,
        patientId: action.payload?.patientId,
      };
      state.patientTherapyData.patientInsurance = getInsuranceState(action.payload?.insurances);
    },
    clearPatientFormData: (state) => {
      state.demographic.demographicData = {};
      state.hypertension.hypertensionData = {};
      state.observation.observationData = {};
      state.hypertensionInfo.formData1 = {
        hasHtnDiagnosisCode: '',
        hasHtnDisorderHistoryCode: '',
        htnDisorders: htnDisordersOptions,
        pathway: '',
        disableNextButton: false,
        payloads: [],
      };
      state.hypertensionInfo.formData2 = {
        htnDiagnosedTypes: hypertensionOptions,
        stepsTaken: stepsTakenConfig,
        secondaryHtnCause: '',
        disableNextButton: false,
        payloads: [],
      };
      state.hypertensionInfo.formData3 = {
        isMedActivelyTitrated: '',
        hasBeenOnHtnMeds: '',
        hasBeenEvaluatedForRDN: '',
        hasBeenReferredForRDN: '',
        rdnProceduralistReferredInfo: {
          firstName: '',
          lastName: '',
          npi: '',
        },
        rdnProceduralistPerformedInfo: {
          firstName: '',
          lastName: '',
          npi: '',
        },
        rdnProcedurePerformedDate: null,
        disableNextButton: false,
        payloads: [],
      };
      state.createdPatientId = null;
      state.createdNameId = null;
      state.createdTelecomId = null;
      state.createdPhoneId = null;
      state.patientTherapyData.patientInsurance = getInsuranceState();
    },
    setPatientHypertensionData: (state, action) => {
      state.hypertension = {
        ...state.hypertension,
        ...action.payload?.data,
      };
    },
    setPatientHypertensionResponseData: (state, action) => {
      state.hypertension.hypertensionResponseData = {
        ...state.hypertension.hypertensionResponseData,
        ...action.payload,
      };
    },
    setTransformedPatientHypertensionResponseData: (state, action) => {
      state.hypertension.transformedPatientHypertensionResponseData = {
        ...state.hypertension.transformedPatientHypertensionResponseData,
        ...action.payload,
      };
    },
    clearPatientHypertensionResponseData: (state) => {
      state.hypertension.hypertensionResponseData = {};
      state.hypertension.transformedPatientHypertensionResponseData = {};
    },
    setPatientHypertensionPayload: (state, action) => {
      const { allHypertensionPayload, disableNextButton, removedHypertensionDiagnosisPayload } =
        action.payload;

      state.hypertension.payload = allHypertensionPayload;
      state.hypertension.disableNextButton = disableNextButton;
      state.hypertension.removedHypertensionDiagnosisPayload = removedHypertensionDiagnosisPayload;
    },
    setPatientObservationData: (state, action) => {
      const {
        data,
        data: { patientId, observationData },
      } = action.payload;

      const getValueQuantity = (label, unit, system) => ({
        value: label?.value,
        unit,
        code: { coding: { system, code: unit }, text: unit },
      });

      const {
        SystolicBP_at_enroll_object,
        DiastolicBP_at_enroll_object,
        Cholesterol_total,
        Cholesterol_HDL,
        Cholesterol_LDL,
      } = observationData || {};

      const getComponentBpPanelAtEnroll = () => {
        const component = [
          {
            code: {
              text: SystolicBP_at_enroll_object?.text,
              coding: [
                {
                  code: SYSTOLIC_BP_AT_ENROLL,
                  system: 'PECC',
                  display: 'Systolic Blood Pressure',
                },
              ],
            },
            value_quantity: getValueQuantity(
              SystolicBP_at_enroll_object,
              'mmHg',
              'http://unitsofmeasure.org',
            ),
          },
          {
            code: {
              text: DiastolicBP_at_enroll_object?.text,
              coding: [
                {
                  code: DIASTOLIC_BP_AT_ENROLL,
                  system: 'PECC',
                  display: 'Diastolic Blood Pressure',
                },
              ],
            },
            value_quantity: getValueQuantity(
              DiastolicBP_at_enroll_object,
              'mmHg',
              'http://unitsofmeasure.org',
            ),
          },
        ];
        return {
          code: BP_PANEL_AT_ENROLL,
          display: 'Blood Pressure at Enrollment',
          component,
          status: DiastolicBP_at_enroll_object?.status,
        };
      };
      const constructObservationPayload = () =>
        [getComponentBpPanelAtEnroll(), Cholesterol_total, Cholesterol_HDL, Cholesterol_LDL]
          .map((item) => {
            if (item?.code === BP_PANEL_AT_ENROLL) {
              return therapyDataPayload(patientId, {
                code: item?.code,
                display: item?.display,
                component: item?.component ?? [],
                status: item?.status ?? 'final',
              });
            }
            return therapyDataPayload(
              patientId,
              {
                code: item?.code,
                display: item?.display,
                status: 'final',
                component: item?.component ?? [],
              },
              // NOTE mmHg unit is hardcoded because the unit we currently get from BE
              // is incorrect (hh[Mg]); product had signed off on this
              getValueQuantity(item, 'mg/dL', 'http://unitsofmeasure.org'),
            );
          })
          .filter((payload) => payload !== null);

      const payload = { payload: constructObservationPayload() };
      state.observation = {
        ...state.observation,
        ...data,
        ...payload,
      };
    },
    setPatientObservationResponseData: (state, action) => {
      state.observation.observationResponseData = {
        ...state.observation.observationResponseData,
        ...action.payload,
      };
    },
    clearPatientDataSectionsByNamesArray: (state, action) => {
      const sections = action.payload;

      if (sections?.length > 1) {
        sections.forEach((section) => {
          state[section] = initialState[section];
        });
      } else if (sections?.length === 1) {
        state[sections[0]] = initialState[sections[0]];
      }
    },
    setCreatedPatientId: (state, action) => {
      state.createdPatientId = action.payload;
    },
    setCreatedNameId: (state, action) => {
      state.createdNameId = action.payload;
    },

    setCreatedTelecomId: (state, action) => {
      state.createdTelecomId = action.payload;
    },

    setCreatedPhoneId: (state, action) => {
      state.createdPhoneId = action.payload;
    },

    setCurrentPatientId: (state, action) => {
      state.currentPatientId = action.payload;
    },

    setTherapyDataFilter: (state, action) => {
      state.therapyDataFilter = action.payload;
    },
    setPatientEngagementOverviewData: (state, action) => {
      state.patientTherapyData.patientJourney.patientEngagementOverview = {
        ...state.patientTherapyData.patientJourney.patientEngagementOverview,
        ...action.payload,
      };
    },
    setPatientPathwayTemplatesResponseData: (state, action) => {
      state.hypertension.patientPathwayTemplatesResponseData = getUniqueEntries([
        { ...action.payload },
        ...state.hypertension.patientPathwayTemplatesResponseData,
      ]);
    },

    clearPatientPathwayTemplatesResponseData: (state) => {
      state.hypertension.patientPathwayTemplatesResponseData = [];
    },

    setPatientKeyBPData: (state, action) => {
      state.patientTherapyData.keyBPData = {
        ...state.patientTherapyData.keyBPData,
        ...action.payload,
      };
    },
    setTherapyChartData: (state, action) => {
      state.patientTherapyData.therapyChartData.averageOverTimeframe = action.payload;
    },
    setTherapyChartHeartRateData: (state, action) => {
      state.patientTherapyData.therapyChartData.heartRate = action.payload;
    },
    setTherapyChartTargetBPData: (state, action) => {
      state.patientTherapyData.therapyChartData.targetBP = {
        ...state.patientTherapyData.therapyChartData.targetBP,
        ...action.payload,
      };
    },
    setPatientSurveyData: (state, action) => {
      state.patientTherapyData.surveyData = {
        ...state.patientTherapyData.surveyData,
        ...action.payload,
      };
    },
    setPatientHTNData: (state, action) => {
      state.patientTherapyData.patientHTNData = {
        ...state.patientTherapyData.patientHTNData,
        ...action.payload,
      };
    },
    setPatientMedicalHistoryData: (state, action) => {
      const { hypertensionDiagnosis = [], isHypertensionDiagnosisUpdate = false } = action.payload;
      const processedHypertensionDiagnosis = isHypertensionDiagnosisUpdate
        ? getUniqueEntries([
            ...state.hypertension.patientMedicalHistoryData.hypertensionDiagnosis,
            ...hypertensionDiagnosis,
          ])
        : hypertensionDiagnosis;
      const {
        /* eslint-disable-next-line no-unused-vars */
        isHypertensionDiagnosisUpdate: _removedHypertensionDiagnosisUpdateStatus,
        ...finalPayload
      } = action.payload;

      state.hypertension.patientMedicalHistoryData = {
        ...state.hypertension.patientMedicalHistoryData,
        ...finalPayload,
      };

      state.hypertension.patientMedicalHistoryData.hypertensionDiagnosis =
        processedHypertensionDiagnosis;
    },
    removeHypertensionDiagnosisItemByKey: (state, action) => {
      const { code = '' } = action.payload;
      const existingHypertensionDiagnosis =
        state.hypertension.patientMedicalHistoryData.hypertensionDiagnosis;
      const newHypertensionDiagnosisArray = existingHypertensionDiagnosis.filter(
        (diagnosis) => diagnosis.code !== code,
      );

      state.hypertension.patientMedicalHistoryData.hypertensionDiagnosis =
        newHypertensionDiagnosisArray;
    },
    setPatientMedicationsData: (state, action) => {
      state.hypertension.patientMedicationsData = {
        ...state.hypertension.patientMedicationsData,
        ...action.payload,
      };
    },
    setPatientInstructionsListData: (state, action) => {
      state.patientInstructionsListData = {
        ...state.patientInstructionsListData,
        ...action.payload,
      };
    },
    setPersonsData: (state, action) => {
      state.personData = {
        ...state.personData,
        ...action.payload,
      };
    },
    setPatientExecuteListsData: (state, action) => {
      state.patientListData.patientExecuteList = action.payload;
    },
    setPatientEnrollmentReasonValueData: (state, action) => {
      state.patientTherapyData.patientEnrollmentReason.value = action.payload;
    },
    setPatientEnrollmentReasonPayloadData: (state, action) => {
      state.patientTherapyData.patientEnrollmentReason.payload = action.payload;
    },
    setPatientInsuranceData: (state, action) => {
      state.patientTherapyData.patientInsurance = getInsuranceState(action.payload);
    },
    setPatientHypertensionFormData1Info: (state, action) => {
      state.hypertensionInfo.formData1 = {
        ...state.hypertensionInfo.formData1,
        ...action.payload,
      };
    },
    setPatientHypertensionFormData2Info: (state, action) => {
      state.hypertensionInfo.formData2 = {
        ...state.hypertensionInfo.formData2,
        ...action.payload,
      };
    },
    setPatientHypertensionFormData3Info: (state, action) => {
      state.hypertensionInfo.formData3 = {
        ...state.hypertensionInfo.formData3,
        ...action.payload,
      };
    },
  },
});

export const {
  clearPatientDataSectionsByNamesArray,
  clearPatientFormData,
  clearPatientHypertensionResponseData,
  clearPatientPathwayTemplatesResponseData,
  removeHypertensionDiagnosisItemByKey,
  setCreatedPatientId,
  setCurrentPatientId,
  setCreatedNameId,
  setCreatedTelecomId,
  setCreatedPhoneId,
  setPatientDemographicData,
  setPatientDemographicResponseData,
  setPatientEngagementOverviewData,
  setPatientExecuteListsData,
  setPatientHTNData,
  setPatientHypertensionData,
  setPatientHypertensionPayload,
  setPatientHypertensionResponseData,
  setPatientEnrollmentReasonValueData,
  setPatientEnrollmentReasonPayloadData,
  setPatientInsuranceData,
  setPatientHypertensionFormData1Info,
  setPatientHypertensionFormData2Info,
  setPatientHypertensionFormData3Info,
  setPatientInstructionsListData,
  setPatientKeyBPData,
  setPatientListByOrgId,
  setPatientMedicalHistoryData,
  setPatientMedicationsData,
  setPatientObservationData,
  setPatientObservationResponseData,
  setPatientPathwayTemplatesResponseData,
  setPatientSurveyData,
  setPersonsData,
  setTherapyChartData,
  setTherapyChartHeartRateData,
  setTherapyChartTargetBPData,
  setTherapyDataFilter,
  setTransformedPatientHypertensionResponseData,
} = patientSlice.actions;
export default patientSlice.reducer;
